import React, { useState, useEffect } from 'react';
import './SavedCards.css';
import { apiRequest, parseResponse, getLoggedInUserId } from '../utils/api';
import { maskCardNumber, normalizeSavedCard } from '../utils/cards';

const SavedCards = ({ onSelectCard, onBack, onAddNew }) => {
  const [savedCards, setSavedCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSavedCards();
  }, []);

  const fetchSavedCards = async () => {
    try {
      setLoading(true);
      setError(null);

      const userId = getLoggedInUserId();
      if (!userId) {
        throw new Error('User ID not found');
      }

      const response = await apiRequest(`/payments/saved-cards/${userId}`, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch saved cards');
      }

      const data = await parseResponse(response);
      const cards = Array.isArray(data) ? data : (data?.data || []);
      setSavedCards((Array.isArray(cards) ? cards : []).map(normalizeSavedCard));
    } catch (err) {
      console.error('Error fetching saved cards:', err);
      setError(err.message || 'Failed to load saved cards');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCard = async (cardId, e) => {
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this card?')) {
      return;
    }

    try {
      const response = await apiRequest(`/payments/saved-cards/${cardId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete card');
      }

      // Remove card from list
      setSavedCards(savedCards.filter(card => card.id !== cardId));
    } catch (err) {
      console.error('Error deleting card:', err);
      alert(err.message || 'Failed to delete card');
    }
  };

  const getCardIcon = (cardType) => {
    const type = (cardType || '').toLowerCase();
    if (type.includes('visa')) {
      return 'VISA';
    } else if (type.includes('master')) {
      return 'MC';
    } else if (type.includes('amex') || type.includes('american')) {
      return 'AMEX';
    } else if (type.includes('rupay')) {
      return 'RUPAY';
    }
    return 'CARD';
  };

  if (loading) {
    return (
      <div className="saved-cards-screen">
        <div className="saved-cards-content">
          <header className="saved-cards-header">
            <button className="back-button" onClick={onBack}>
              <img
                src={process.env.PUBLIC_URL + '/images/back_button.svg'}
                alt="Back"
              />
            </button>
            <h1 className="saved-cards-title">Saved Cards</h1>
          </header>
          <div className="saved-cards-loading">
            <div className="loading-spinner"></div>
            <p>Loading saved cards...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="saved-cards-screen">
      <div className="saved-cards-content">
        <header className="saved-cards-header">
          <button className="back-button" onClick={onBack}>
            <img
              src={process.env.PUBLIC_URL + '/images/back_button.svg'}
              alt="Back"
            />
          </button>
          <h1 className="saved-cards-title">Saved Cards</h1>
        </header>

        {error && (
          <div className="saved-cards-error">
            <p>{error}</p>
            <button className="retry-button" onClick={fetchSavedCards}>
              Retry
            </button>
          </div>
        )}

        {!error && savedCards.length === 0 && (
          <div className="saved-cards-empty">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
              <path d="M2 10H22" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <p>No saved cards</p>
            <p className="empty-subtitle">Add a card to make payments faster</p>
          </div>
        )}

        {!error && savedCards.length > 0 && (
          <div className="saved-cards-list">
            {savedCards.map((card) => (
              <div
                key={card.id || card.cardId}
                className="saved-card-item"
                onClick={() => onSelectCard(card)}
              >
                <div className="card-icon">
                  <span className="card-type-badge">{getCardIcon(card.cardType || card.brand)}</span>
                </div>
                <div className="card-info">
                  <div className="card-number">{maskCardNumber(card.last4)}</div>
                  <div className="card-details">
                    <span className="card-name">{card.name}</span>
                    {card.expiryMonth && card.expiryYear && (
                      <span className="card-expiry">
                        {String(card.expiryMonth).padStart(2, '0')}/{String(card.expiryYear).slice(-2)}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  className="delete-card-button"
                  onClick={(e) => handleDeleteCard(card.id || card.cardId, e)}
                  aria-label="Delete card"
                >
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        <button className="add-card-button" onClick={onAddNew}>
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          Add New Card
        </button>
      </div>
    </div>
  );
};

export default SavedCards;
